import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Navigation, Star, MapPin, AlertCircle, Loader, CheckCircle, Users, Clock, Activity } from 'lucide-react';
import { animations } from '../animations';
import PlaceInput from '../../VehicleType/PlaceInput';
import {  Header,
    Body,
    FormGroup,
    Label,
    Error,
    SuggestedCard,
    ButtonGroup,
    Button } from '../StyledComponents';
import { ProfessionalLoader } from '../components/ProfessionalLoader';

// Enhanced styled components to match QuestionsStep
const StepContainer = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  border: 2px solid #f1f5f9;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: #e0e7ff;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3b82f6, #1d4ed8);
    opacity: 1;
    transition: opacity 0.3s ease;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 1.5rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.5;
  padding-right: 2rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: #3b82f6;
  }
`;

const SeverityIndicator = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  border: 2px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  margin-bottom: 1rem;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
  }

  .severity-badge {
    padding: 0.5rem 1rem;
    border-radius: 25px;
    font-weight: 600;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &.severe {
      background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
      color: #991b1b;
      border: 2px solid #f87171;
    }

    &.moderate {
      background: linear-gradient(135deg, #fffbeb 0%, #fde68a 100%);
      color: #92400e;
      border: 2px solid #f59e0b;
    }
  }
`;

const EnhancedSuggestedCard = styled(motion.div)`
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 2px solid #f59e0b;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #f59e0b, #d97706);
  }

  .header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;

    svg {
      color: #d97706;
    }

    h3 {
      margin: 0;
      color: #92400e;
      font-size: 1.125rem;
      font-weight: 600;
    }
  }
`;

const PlaceInputContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  border: 2px solid #e2e8f0;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1000;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
  }

  &:focus-within {
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
    z-index: 1001;
  }

  .input-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    font-weight: 600;
    color: #334155;

    svg {
      color: #3b82f6;
    }
  }

  /* Ensure Google Places autocomplete dropdown appears above other elements */
  .css-26l3qy-menu,
  .css-1pahdxg-control,
  [class*="menu"],
  [class*="MenuList"],
  [class*="option"],
  [class*="Menu"],
  [class*="menu-list"],
  [class*="control"],
  div[class*="__menu"],
  div[class*="__menu-list"],
  div[class*="__option"] {
    z-index: 9999 !important;
  }

  /* Target react-select dropdown menu - more specific */
  & > div > div[class*="menu"],
  & div[class*="__menu"],
  & div[class*="__menu-list"],
  & div[class*="__menu-portal"] {
    z-index: 9999 !important;
  }

  /* Global override for Google Places dropdown */
  & .gm-style-iw,
  & .pac-container {
    z-index: 9999 !important;
  }

  /* Additional overrides for react-google-places-autocomplete */
  .react-google-autocomplete {
    position: relative;
    z-index: 9999;
  }

  /* Ensure all dropdown elements are visible */
  & * {
    &[class*="menu"],
    &[class*="Menu"],
    &[class*="dropdown"],
    &[class*="Dropdown"],
    &[class*="list"],
    &[class*="List"] {
      z-index: 9999 !important;
      position: relative;
    }
  }
`;

const StepCounter = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 2rem;
  display: inline-block;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
`;

const InstructionCard = styled(motion.div)`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #3b82f6;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  
  svg {
    color: #1e40af;
    flex-shrink: 0;
    margin-top: 0.1rem;
  }
  
  .content {
    h4 {
      margin: 0 0 0.5rem 0;
      color: #1e40af;
      font-weight: 600;
    }
    
    p {
      margin: 0;
      color: #1e40af;
      font-size: 0.9rem;
      line-height: 1.5;
    }
  }
`;

export const DestinationStep = ({
  calculatedSeverity,
  suggestedDestination,
  destination,
  destinationError,
  onDestinationChange,
  onAcceptSuggestion,
  onPrevious,
  onNext,
  isLoading = false
}) => {
  // The button should only be enabled when destination is an object with coordinates
  // This means a place was actually selected from the dropdown, not just typed
  const isDestinationSelected = destination && typeof destination === 'object' && destination.name && destination.lat && destination.lng;
  const canProceed = isDestinationSelected;

  return (
    <motion.div {...animations.pageTransition}>
      {isLoading && (
        <ProfessionalLoader
          isVisible={isLoading}
          title="Processing Emergency Request"
          subtitle="Connecting you with emergency services and finding available ambulances..."
          showSteps={true}
          currentStep={1}
        />
      )}
      <Header>
        <Navigation />
        <h2>Choose Destination</h2>
      </Header>
      <Body>
        <StepCounter>
          Step 3 of 4 - Destination Selection
        </StepCounter>

        <InstructionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <MapPin size={20} />
          <div className="content">
            <h4>Select Your Destination</h4>
            {/* <p>Choose the hospital or medical facility where the patient should be transported. We'll recommend the best option based on the severity assessment.</p> */}
          </div>
        </InstructionCard>

        {/* Patient Severity Section */}
        <StepContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <SectionTitle>
            <AlertCircle size={20} />
            Patient Severity Assessment
          </SectionTitle>
          
          <SeverityIndicator>
            <AlertCircle size={16} />
            <span>Assessment:</span>
            {calculatedSeverity === 102 && (
              <div className="severity-badge severe">
                <AlertCircle size={14} />
                Severe
              </div>
            )}
            {calculatedSeverity === 101 && (
              <div className="severity-badge moderate">
                <AlertCircle size={14} />
                Moderate
              </div>
            )}
            {!calculatedSeverity && (
              <div className="severity-badge moderate">
                <AlertCircle size={14} />
                Assessment Pending
              </div>
            )}
          </SeverityIndicator>
        </StepContainer>

        {/* Suggested Destination Section */}
        {suggestedDestination && (
          <EnhancedSuggestedCard
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="header">
              <Star size={20} />
              <h3>Recommended Hospital</h3>
            </div>
            <p style={{ color: '#92400e', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Based on your severity assessment and location, we recommend:
            </p>
            <Button
              variant="outline"
              onClick={onAcceptSuggestion}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{
                background: 'white',
                color: '#92400e',
                border: '2px solid #d97706',
                fontWeight: '600'
              }}
            >
              <MapPin size={16} />
              {suggestedDestination.name}
            </Button>
          </EnhancedSuggestedCard>
        )}

        {/* Destination Selection Section */}
        <StepContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <SectionTitle>
            <MapPin size={20} />
            Choose Destination Hospital
          </SectionTitle>
          
          <PlaceInputContainer>
            <div className="input-label">
              <MapPin size={16} />
              Search for hospital or medical facility
            </div>
            <PlaceInput
              value={destination}
              onChanges={onDestinationChange}
              point={destination}
              error={destinationError}
            />
            {destinationError && (
              <Error style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AlertCircle size={14} />
                {destinationError}
              </Error>
            )}
          </PlaceInputContainer>
        </StepContainer>

        <ButtonGroup>
          <Button 
            variant="secondary" 
            onClick={onPrevious}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Previous
          </Button>
          <Button
            variant="primary"
            onClick={onNext}
            disabled={!canProceed}
            whileHover={!canProceed ? {} : { scale: 1.02 }}
            whileTap={!canProceed ? {} : { scale: 0.98 }}
            style={{
              background: canProceed 
                ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' 
                : undefined,
              opacity: canProceed ? 1 : 0.6
            }}
          >
            {canProceed 
              ? 'Continue to Summary' 
              : 'Select Destination to Continue'
            }
          </Button>
        </ButtonGroup>
      </Body>
    </motion.div>
  );
};
